<script>
	// NPM Modules
	import { createEventDispatcher } from 'svelte';
	import dayjs from 'dayjs';
	// Stores
	import SportStore from '@/Stores/Sport/SportStore.js';
	import SportEventStore from '@/Stores/Sport/SportEventStore';
	import CompetitionStore from '@/Stores/Sport/CompetitionStore';
	// Components
	import Datepicker from '@/Components/Datepicker.svelte';
	import Modal from '@/Components/Modal.svelte';
	import TextInput from '@/Components/TextInput.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import SelectInput from '@/Components/SelectInput.svelte';
	import CheckBox from '@/Components/CheckBox.svelte';
	// Services
	import { notify } from '@/Services/Notify';

	export let sport;
	export let open;

	const dispatch = createEventDispatcher();

	let sending = false;
	let errors = null;
	let competitions = [];
	let sports = [];
	let selectedSportId;
	let selectedSportName;

	let sportEventForm = {
		sport_id: (sport && sport.id) ?? null,
		competition_id: null,
		name: null,
		start_time: null,
		outright: false
	};

	let sportEventDate = dayjs().format('YYYY-MM-DD');
	let sportEventTime = dayjs().format('HH:mm');

	$: sportEventStart = dayjs(sportEventDate + ' ' + sportEventTime + ':00');
	$: sportEventStart && updateDateTimes();
	$: ((sport && sport.id) || selectedSportId) && getCompetitions();
	$: selectedSportName = $SportStore.where('id', selectedSportId).pluck('name').first();

	function updateDateTimes() {
		sportEventForm.start_time = dayjs(sportEventStart).format('YYYY-MM-DDTHH:mm:ssZ');
	}

	function getSports() {
		let sportsTemp = [];
		$SportStore.sortBy('name').each((sport) => {
			sportsTemp.push({
				id: sport.id,
				name: sport.name
			});
		});
		sports = sportsTemp;
	}

	getSports();

	async function getCompetitions() {
		CompetitionStore.getCompetitionsForSport(selectedSportId).then(() => {
			let competitionsTemp = [];
			$CompetitionStore.sortBy('name').each((competition) => {
				competitionsTemp.push({
					id: competition.id,
					name: competition.name
				});
			});
			competitions = competitionsTemp;
		});
	}

	function createSportsEvent() {
		sending = true;
		SportEventStore.createEvent(sportEventForm)
			.then(() => {
				notify({
					type: 'success',
					title: 'Manual market type created',
					message: `Manual market type successfully created for ${(sport && sport.name) ?? selectedSportName}!`,
					timeout: 3
				});
				dispatch('created');
				open = false;
				cleanURLParameter('showCreateSportEvent');
				sportEventForm = {
					sport_id: (sport && sport.id) ?? null,
					competition_id: null,
					name: null,
					start_time: null,
					outright: false
				};
			})
			.catch((e) => {
				errors = e;
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Problem creating manual event',
					timeout: 3
				});
			})
			.finally(() => (sending = false));
	}

	// Function to clean the URL parameter
	function cleanURLParameter(param) {
		const url = new URL(window.location.href);
		url.searchParams.delete(param);
		window.history.replaceState({}, '', url);
	}

	function closeEvent() {
		open = false;
		cleanURLParameter('showCreateSportEvent');
	}
</script>

{#if open}
	<Modal on:close={closeEvent}>
		<h2 slot="header" class="pt-2 text-xl font-bold text-center">
			Create event for {(sport && sport.name) ?? selectedSportName ?? '...'}
		</h2>
		<div class="flex flex-col w-full px-4 pt-2 text-center">
			{#if !sport}
				<SelectInput
					label="Sport"
					className="py-4 w-full text-left"
					name="sportEventSportId"
					bind:value={selectedSportId}
				>
					<option value={null}>Please select a sport</option>
					{#each sports as sport (sport.id)}
						{#if sport.id === parseInt(sportEventForm.sport_id)}
							<option value={sport.id} selected>{sport.name}</option>
						{:else}
							<option value={sport.id}>{sport.name}</option>
						{/if}
					{/each}
				</SelectInput>
			{/if}
			<TextInput
				label="Event Name"
				name="sportEventName"
				required={true}
				className="py-4 w-full text-left"
				errors={errors && errors.name}
				placeholder="Enter event name"
				bind:value={sportEventForm.name}
			/>
			<SelectInput
				label="Competition"
				className="py-4 w-full text-left"
				name="sportEventCompetitionId"
				bind:value={sportEventForm.competition_id}
			>
				<option value={null}>Please select a competition</option>
				{#each competitions as competition (competition.id)}
					{#if competition.id === parseInt(sportEventForm.competition_id)}
						<option value={competition.id} selected>{competition.name}</option>
					{:else}
						<option value={competition.id}>{competition.name}</option>
					{/if}
				{/each}
			</SelectInput>
			<div class="text-left mb-4">
				<label class="form-label" for="sportEventStartTime">Start Time: </label>
				<div class="flex flex-row">
					<div class="flex-shrink">
						<Datepicker
							selected={sportEventDate}
							displayFormat="DD-MM-YYYY"
							dateChosen={(date) => {
								sportEventDate = dayjs(date).format('YYYY-MM-DD');
							}}
						/>
					</div>
					<TextInput bind:value={sportEventTime} name="sportEventStartTime" type="time" />
				</div>
			</div>
			<div class="py-4 w-full text-left">
				<CheckBox text="Outright" bind:checked={sportEventForm.outright} />
			</div>
			<LoadingButton on:click={() => createSportsEvent()} loading={sending} className="my-4 btn btn-primary">
				{sending ? 'Creating' : 'Create'}
			</LoadingButton>
			<button class="mb-4 btn btn-muted" on:click={() => (open = false)}>Cancel</button>
		</div>
	</Modal>
{/if}
